import { POSSIBLE_CSV_MIMETYPES } from "constant/app";
import { ConnectedBulkDeviceEditSample } from "fleet/controller/device/connected-bulk-device-edit-sample";
import get from "lodash/get";
import PropTypes from "prop-types";
import React from "react";
import {
  Button,
  Col,
  FormCheck,
  FormLabel,
  FormControl,
  FormGroup,
  FormText,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { BackButton } from "ui/button/back";
import { Features } from "ui/features";
import { Notification } from "ui/notification";
import { getErrorDetails, getErrorMessage, getFormErrors } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class BulkDeviceEditPage extends React.PureComponent {
  static get propTypes() {
    return {
      appParamOptions: PropTypes.array.isRequired,
      bulkEditDevices: PropTypes.func.isRequired,
      devicePropertyOptions: PropTypes.array.isRequired,
      flagOptions: PropTypes.array.isRequired,
      targetOptions: PropTypes.array.isRequired,
    };
  }

  constructor() {
    super();

    this.state = {
      ...getFormErrors(),
      errorDetails: "",
      errorMessage: "",
      isCustomConfig: false,
      successMessage: "",
      selectedAppParams: [],
      selectedDeviceFlags: [],
      selectedDeviceProps: [],
      selectedTargets: [],
    };

    this.file = null;

    this._handleSuccess = this._handleSuccess.bind(this);
    this._onAppParamSelect = this._onAppParamSelect.bind(this);
    this._onDeviceFlagSelect = this._onDeviceFlagSelect.bind(this);
    this._onDevicePropSelect = this._onDevicePropSelect.bind(this);
    this._onTagSelect = this._onTagSelect.bind(this);
    this._resetMessages = this._resetMessages.bind(this);
    this._startUpload = this._startUpload.bind(this);
    this._submit = this._submit.bind(this);
    this._toggleCustomConfig = this._toggleCustomConfig.bind(this);
  }

  _handleSuccess(response) {
    const successMessage = (
      <div>
        <ul>
          {response.map((r, i) => (
            <li key={i}>{r.message}</li>
          ))}
        </ul>
        <span>
          Note that offline devices will reflect any changes when a device is
          back online.
          <br />
          For any flag updates, the device must be restarted for changes to be
          implemented.
        </span>
      </div>
    );

    this.setState({
      successMessage,
      errorDetails: "",
      errorMessage: "",
      ...getFormErrors(),
    });
  }

  _onDevicePropSelect(opts) {
    this.setState({ selectedDeviceProps: opts });
  }

  _onAppParamSelect(opts) {
    this.setState({ selectedAppParams: opts });
  }

  _onDeviceFlagSelect(opts) {
    this.setState({ selectedDeviceFlags: opts });
  }

  _onTagSelect(opts) {
    this.setState({ selectedTargets: opts });
  }

  _resetMessages() {
    this.setState({
      errorDetails: "",
      errorMessage: "",
      successMessage: "",
      ...getFormErrors(),
    });
  }

  _startUpload(file) {
    this.setState(
      {
        uploading: true,
        successMessage: "",
        errorDetails: "",
        errorMessage: "",
      },
      this._uploadAction.bind(this, file),
    );
  }

  _isValidFileType(filetype) {
    // On Windows/Chrome file.type is blank
    return !filetype || POSSIBLE_CSV_MIMETYPES.includes(filetype);
  }

  _submit() {
    const file = get(this.file, "files.0", null);
    if (!file) {
      this.setState({
        formErrors: {
          file: "Please select a file.",
        },
      });
      return;
    }

    if (!this._isValidFileType(file.type)) {
      this.setState({
        formErrors: {
          file: "A valid .csv file is required.",
        },
      });
      return;
    }

    this._startUpload(file);
  }

  _toggleCustomConfig(event) {
    const { targetOptions } = this.props;
    const { value } = event.target;
    const isCustomConfig = value === "custom";
    // When switching back to custom template page, by default the _all tag
    // should be selected.
    const selectedTargets = isCustomConfig
      ? targetOptions.filter((target) => target.label === "tag: _all")
      : [];

    this.setState({
      isCustomConfig,
      selectedTargets,
      selectedDeviceProps: [],
      selectedAppParams: [],
      selectedDeviceFlags: [],
    });
  }

  _uploadAction(file) {
    const { bulkEditDevices } = this.props;

    const serviceArgs = { file };

    return bulkEditDevices(serviceArgs)
      .then(this._handleSuccess)
      .catch((resp) => {
        const errorDetails = getErrorDetails(resp);
        const errorMessage = errorDetails
          ? "Error details"
          : getErrorMessage(resp);

        this.setState({
          errorDetails,
          errorMessage,
          ...getFormErrors(resp),
        });
      })
      .finally(() => {
        this.setState({ uploading: false });
      });
  }

  _renderCustomOptions() {
    const {
      appParamOptions,
      devicePropertyOptions,
      flagOptions,
      targetOptions,
    } = this.props;
    const {
      selectedDeviceProps,
      selectedAppParams,
      selectedDeviceFlags,
      selectedTargets,
    } = this.state;

    return (
      <Row>
        <Col xs={8}>
          <FormLabel>
            Select the columns and rows you would like to edit
          </FormLabel>
          <FormGroup controlId="device-properties">
            <FormLabel>Device Properties (Columns)</FormLabel>
            <Select
              name="device-properties"
              onChange={this._onDevicePropSelect}
              options={devicePropertyOptions}
              value={selectedDeviceProps}
              isMulti
            />
          </FormGroup>
          <FormGroup controlId="application-parameters">
            <FormLabel>Application Parameters (Columns)</FormLabel>
            <Select
              name="application-parameters"
              onChange={this._onAppParamSelect}
              options={appParamOptions}
              value={selectedAppParams}
              isMulti
            />
          </FormGroup>
          <FormGroup controlId="device-flags">
            <FormLabel>Device Flags (Columns)</FormLabel>
            <Select
              name="device-flags"
              onChange={this._onDeviceFlagSelect}
              options={flagOptions}
              value={selectedDeviceFlags}
              isMulti
            />
          </FormGroup>
          <FormGroup controlId="target-tags">
            <FormLabel>Target Networks & Devices (Rows)</FormLabel>
            <Select
              name="target-tags"
              onChange={this._onTagSelect}
              options={targetOptions}
              value={selectedTargets}
              isMulti
            />
          </FormGroup>
        </Col>
      </Row>
    );
  }

  render() {
    const {
      errorDetails,
      errorMessage,
      formErrors,
      isCustomConfig,
      selectedAppParams,
      selectedDeviceFlags,
      selectedDeviceProps,
      selectedTargets,
      successMessage,
      uploading,
    } = this.state;

    const isBulkEditEnabled = Features.isEnabled(
      Features.constants.DEVICE_BULK_EDIT,
    );

    const selectedOpts = {
      isCustomConfig,
      appParams: selectedAppParams,
      flags: selectedDeviceFlags,
      properties: selectedDeviceProps,
      targets: selectedTargets.map((target) => target.value),
    };

    return (
      <Page title="Bulk Edit Devices">
        <Box noHeader={true}>
          <Row>
            <Col xs={12}>
              {isBulkEditEnabled && (
                <FormGroup controlId="config">
                  <FormLabel>
                    <strong>Choose from the two options below</strong>
                  </FormLabel>
                  <br />
                  <FormCheck
                    checked={!isCustomConfig}
                    inline
                    label="Export Device Configuration"
                    name="config"
                    onChange={this._toggleCustomConfig}
                    type="radio"
                    value="default"
                  />
                  <FormCheck
                    checked={isCustomConfig}
                    inline
                    label="Export Custom Device Configuration"
                    name="config"
                    onChange={this._toggleCustomConfig}
                    type="radio"
                    value="custom"
                  />
                </FormGroup>
              )}
            </Col>
          </Row>
          {isCustomConfig && this._renderCustomOptions()}
          <Row>
            <Col xs={6}>
              <Notification
                error={errorMessage}
                errorChildren={errorDetails}
                success={successMessage}
                successChildren={<Link to="/devices">Device List</Link>}
              />
              <Row className="mb-25">
                <Col xs={12}>
                  <ConnectedBulkDeviceEditSample {...selectedOpts} />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FormGroup
                    controlId="file"
                  >
                    <FormLabel>Upload .csv file</FormLabel>
                    <FormControl
                      type="file"
                      accept=".csv, .txt, text/plain, text/csv"
                      ref={(elem) => {
                        this.file = elem;
                      }}
                      onChange={this._resetMessages}
                      name="file"
                      disabled={uploading}
                      isInvalid={formErrors.file ? "error" : null}
                    />
                    <FormText>
                      <p>Select the .csv file of devices you want to edit.</p>
                    </FormText>
                    {formErrors.file && (
                      <FormText className="extra-help text-danger" name="file">
                        {formErrors.file}
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col
              xs={12}
              className={isBulkEditEnabled ? "button-spacing text-right" : ""}
            >
              <BackButton />
              <Button
                variant="primary"
                onClick={this._submit}
                disabled={uploading}
              >
                Update
              </Button>
            </Col>
          </Row>
        </Box>
      </Page>
    );
  }
}

export { BulkDeviceEditPage };
